import { Injectable, computed, signal } from '@angular/core';

import { SegmentedOption } from 'src/app/@core/components/ui/segmented/segmented.component';
import { USER_ROLE } from 'src/app/@core/models/enums';
import { IUser, IWorkspace, SHARE_ACCESS } from 'src/app/@core/models/model';

import { DateRange, PeriodFilter, WorkspaceViewMode } from '../models/workspace-overview.model';

/**
 * Workspace Overview State Service
 *
 * Single source of truth for all dashboard-level signals. Provided at the
 * `WorkspaceOverviewComponent` level (component-scoped DI) so the same
 * instance is shared by every widget in the subtree without leaking to the
 * rest of the app.
 *
 * Widgets inject this service directly instead of receiving `@Input()` props,
 * which means the container does not need to thread values down through every
 * level.
 *
 * Usage in a widget:
 * ```typescript
 * constructor(private state: WorkspaceOverviewStateService) {}
 *
 * readonly data = signal<MyWidgetData | null>(null);
 *
 * constructor(...) {
 *   effect(() => {
 *     const mode = this.state.viewMode();
 *     const period = this.state.period();
 *     this.fetch(mode, period);
 *   });
 * }
 * ```
 */
@Injectable()
export class WorkspaceOverviewStateService {
    // ─── Signals ──────────────────────────────────────────────────────────────

    /** Active scope: personal (user-only data) or admin (org-wide). */
    readonly viewMode = signal<WorkspaceViewMode>(WorkspaceViewMode.Personal);

    /** Current workspace ID — set once during init() and stable for the lifetime of the dashboard. */
    readonly workspaceId = signal<string>('');

    /** Active period filter. Defaults to Month to match Figma. */
    readonly period = signal<PeriodFilter>(PeriodFilter.Month);

    /**
     * Custom date range — non-null only when `period === Custom` and both
     * ends have been picked.
     */
    readonly customRange = signal<DateRange | null>(null);

    // ─── Computed ─────────────────────────────────────────────────────────────

    /**
     * The resolved date range to pass to API calls. `null` for standard
     * periods (the backend derives the window from the `period` param).
     */
    readonly effectiveRange = computed<DateRange | null>(() =>
        this.period() === PeriodFilter.Custom ? this.customRange() : null
    );

    /**
     * True when the admin-only Feedback & Quality widget should render.
     * Container uses this to conditionally mount the widget.
     */
    readonly showFeedbackQuality = computed(() =>
        this.viewMode() === WorkspaceViewMode.Admin
    );

    // ─── RBAC ─────────────────────────────────────────────────────────────────

    /** Set by the container once the current user is known. */
    canSwitchView = false;

    // ─── Select-button option arrays ──────────────────────────────────────────

    /** Period options for `<app-select-button>` (label-only per design). */
    readonly periodOptions: SegmentedOption[] = [
        { label: 'Last 7d', value: PeriodFilter.Week },
        { label: 'Last 30d', value: PeriodFilter.Month },
        { label: 'Last 90d', value: PeriodFilter.Quarter },
        { label: 'Custom', value: PeriodFilter.Custom },
    ];

    /**
     * View-mode options built after `init()` is called. Only contains the
     * Admin option when the current user has the required RBAC role.
     */
    viewModeOptions: SegmentedOption[] = [];

    // ─── Initialisation ───────────────────────────────────────────────────────

    /**
     * Must be called by the container in its constructor once the current user
     * is available. Sets up RBAC and the view-mode option list.
     */
    init(user: IUser): void {
        const ws = user.currentWorkspace;
        this.workspaceId.set(typeof ws === 'string' ? ws : (ws as IWorkspace)._id as string);

        this.canSwitchView =
            [SHARE_ACCESS.OWNER, SHARE_ACCESS.ADMIN].includes(user.workspaceRole) ||
            [USER_ROLE.ADMIN, USER_ROLE.SUPER_ADMIN].includes(user.role);

        this.viewModeOptions = [
            { label: 'User view', value: WorkspaceViewMode.Personal, icon: 'pi pi-user', tooltip: 'User' },
            ...(this.canSwitchView
                ? [{ label: 'Admin view', value: WorkspaceViewMode.Admin, icon: 'pi pi-building', tooltip: 'Organization' }]
                : []),
        ];
    }

    // ─── Mutators (called by the container from the template) ────────────────

    /**
     * Switch the view scope. Ignores the Admin value when the user lacks the
     * required RBAC role (defensive — the button is hidden anyway).
     */
    setViewMode(mode: WorkspaceViewMode | string): void {
        const next = mode as WorkspaceViewMode;
        if (!this.canSwitchView && next === WorkspaceViewMode.Admin) return;
        this.viewMode.set(next);
    }

    /**
     * Switch the period. Clears any custom date range when leaving Custom.
     */
    setPeriod(period: PeriodFilter | string): void {
        const next = period as PeriodFilter;
        this.period.set(next);
        if (next !== PeriodFilter.Custom) {
            this.customRange.set(null);
        }
    }

    /**
     * Called by the container's date picker. Sets `customRange` once both
     * ends of the range are selected.
     */
    onCustomDatesChange(value: Date | Date[] | (Date | null)[] | null): void {
        if (Array.isArray(value) && value.length === 2 && value[0] && value[1]) {
            this.customRange.set({ from: value[0] as Date, to: value[1] as Date });
        } else {
            this.customRange.set(null);
        }
    }
}
